// ============================================================================
// /onboard/:token — PUBLIC sub-onboarding portal (N3, B3). No auth shell.
// Phone-first (380px), EN/ES. Stepper: business confirm → COI → W-9 →
// direct deposit → sign agreement (email OTP + consent, native e-sign).
// Steps are resumable — meta returns per-step completion.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Building2, CheckCircle2, FileText, Landmark, Loader2, PenLine, ShieldCheck,
} from 'lucide-react';
import {
  PortalError,
  fileToBase64,
  portalPublic,
  type PortalMeta,
} from './lib';

type Lang = 'en' | 'es';

const STR: Record<Lang, Record<string, string>> = {
  en: {
    title: 'Subcontractor Setup',
    subtitle: 'Terminal Cleaning Services',
    loading: 'Loading…',
    invalid: 'This link is not valid. Contact your TCS representative.',
    expired: 'This link has expired. Contact your TCS representative for a new one.',
    done_all: 'All set! Your registration is complete. TCS will be in touch.',
    step_business: 'Confirm your business',
    step_coi: 'Certificate of Insurance',
    step_w9: 'IRS Form W-9',
    step_dd: 'ACH payment authorization',
    dd_intro: 'This authorizes TCS to pay your business by direct bank transfer (ACH).',
    step_sign: 'Sign your agreement',
    legal_name: 'Legal business name',
    go_by: 'Business name you go by (d/b/a)',
    confirm_check: 'I confirm this business information is correct.',
    confirm_btn: 'Confirm',
    upload_hint: 'PDF or photo (JPG/PNG), max 10 MB.',
    upload_coi_label: 'Upload your current COI',
    upload_w9_label: 'Upload your completed W-9',
    upload_btn: 'Upload',
    dd_holder: 'Name on the account',
    dd_bank: 'Bank name',
    dd_routing: 'Routing number (9 digits)',
    dd_account: 'Account number',
    dd_type: 'Account type',
    dd_checking: 'Checking',
    dd_savings: 'Savings',
    dd_note: 'Your bank details go into a secure document only TCS HR can open. They are never stored in plain form.',
    dd_btn: 'Submit ACH authorization',
    sign_intro: 'Verify your identity, then sign the Building Services Operating Agreement.',
    send_code: 'Email me a code',
    code_label: '6-digit code',
    verify_btn: 'Verify',
    verified: 'Identity verified.',
    signed_name: 'Type your full legal name to sign',
    consent: 'I agree to sign this agreement electronically (ESIGN/UETA).',
    sign_btn: 'Sign agreement',
    step_done: 'Done',
    otp_unavailable: 'Email delivery is not configured yet — contact TCS to finish signing.',
    error_generic: 'Something went wrong. Please try again.',
  },
  es: {
    title: 'Registro de Subcontratista',
    subtitle: 'Terminal Cleaning Services',
    loading: 'Cargando…',
    invalid: 'Este enlace no es válido. Contacte a su representante de TCS.',
    expired: 'Este enlace ha expirado. Pida uno nuevo a su representante de TCS.',
    done_all: '¡Listo! Su registro está completo. TCS se pondrá en contacto.',
    step_business: 'Confirme su negocio',
    step_coi: 'Certificado de Seguro (COI)',
    step_w9: 'Formulario W-9 del IRS',
    step_dd: 'Autorización de pago ACH',
    dd_intro: 'Esto autoriza a TCS a pagar a su negocio por transferencia bancaria directa (ACH).',
    step_sign: 'Firme su acuerdo',
    legal_name: 'Nombre legal del negocio',
    go_by: 'Nombre comercial (d/b/a)',
    confirm_check: 'Confirmo que esta información es correcta.',
    confirm_btn: 'Confirmar',
    upload_hint: 'PDF o foto (JPG/PNG), máx 10 MB.',
    upload_coi_label: 'Suba su COI vigente',
    upload_w9_label: 'Suba su W-9 completado',
    upload_btn: 'Subir',
    dd_holder: 'Nombre en la cuenta',
    dd_bank: 'Nombre del banco',
    dd_routing: 'Número de ruta (9 dígitos)',
    dd_account: 'Número de cuenta',
    dd_type: 'Tipo de cuenta',
    dd_checking: 'Corriente',
    dd_savings: 'Ahorros',
    dd_note: 'Sus datos bancarios van a un documento seguro que solo RR.HH. de TCS puede abrir. Nunca se guardan en texto plano.',
    dd_btn: 'Enviar autorización ACH',
    sign_intro: 'Verifique su identidad y firme el Acuerdo de Servicios.',
    send_code: 'Enviarme un código',
    code_label: 'Código de 6 dígitos',
    verify_btn: 'Verificar',
    verified: 'Identidad verificada.',
    signed_name: 'Escriba su nombre legal completo para firmar',
    consent: 'Acepto firmar este acuerdo electrónicamente (ESIGN/UETA).',
    sign_btn: 'Firmar acuerdo',
    step_done: 'Listo',
    otp_unavailable: 'El envío de correo no está configurado — contacte a TCS para terminar la firma.',
    error_generic: 'Algo salió mal. Intente de nuevo.',
  },
};

const inputCls =
  'block w-full rounded-md border border-tcs-border-strong bg-tcs-field px-3 py-3 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';
const btnCls =
  'inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-tcs-inverse px-4 py-3 text-base font-semibold text-tcs-inverse-ink disabled:opacity-50';

export function SubOnboardPortal() {
  const { token } = useParams<{ token: string }>();
  const [lang, setLang] = useState<Lang>('en');
  const t = useCallback((k: string) => STR[lang][k] ?? k, [lang]);

  const [meta, setMeta] = useState<PortalMeta | null>(null);
  const [fatal, setFatal] = useState<'invalid' | 'expired' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const m = await portalPublic<PortalMeta>({ action: 'meta', token });
      setMeta(m);
      setFatal(null);
    } catch (err) {
      if (err instanceof PortalError && err.status === 410) setFatal('expired');
      else setFatal('invalid');
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ---- per-step local state ----
  const [legalName, setLegalName] = useState('');
  const [goBy, setGoBy] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [coiFile, setCoiFile] = useState<File | null>(null);
  const [w9File, setW9File] = useState<File | null>(null);
  const [dd, setDd] = useState({
    holder: '', bank: '', routing: '', account: '', type: 'checking',
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpUnavailable, setOtpUnavailable] = useState(false);
  const [code, setCode] = useState('');
  const [signedName, setSignedName] = useState('');
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (meta) {
      setLegalName((v) => v || meta.legal_business_name || '');
      setGoBy((v) => v || meta.go_by_name || '');
    }
  }, [meta]);

  const allDone = useMemo(
    () =>
      !!meta &&
      meta.steps.business_confirmed &&
      meta.steps.coi_uploaded &&
      meta.steps.w9_uploaded &&
      meta.steps.direct_deposit_submitted &&
      meta.steps.agreement_signed,
    [meta],
  );

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(
        err instanceof PortalError && err.message !== err.code
          ? err.message
          : t('error_generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!token || fatal === 'invalid') {
    return <Shell lang={lang} setLang={setLang} t={t}>{t('invalid')}</Shell>;
  }
  if (fatal === 'expired') {
    return <Shell lang={lang} setLang={setLang} t={t}>{t('expired')}</Shell>;
  }
  if (!meta) {
    return (
      <Shell lang={lang} setLang={setLang} t={t}>
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('loading')}
        </span>
      </Shell>
    );
  }

  return (
    <Shell lang={lang} setLang={setLang} t={t} business={meta.legal_business_name ?? meta.go_by_name}>
      {allDone && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-st-ok-border-strong bg-st-ok-bg p-3 text-sm text-st-ok-fg-strong">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> {t('done_all')}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md border border-st-danger-border-strong bg-st-danger-bg p-3 text-sm text-st-danger-fg-strong">
          {error}
        </div>
      )}

      {/* Step 1 — business confirm */}
      <Step
        icon={<Building2 className="h-5 w-5" />}
        title={t('step_business')}
        done={meta.steps.business_confirmed}
        doneLabel={t('step_done')}
      >
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            {t('legal_name')}
          </span>
          <input className={inputCls} value={legalName} onChange={(e) => setLegalName(e.target.value)} />
        </label>
        <label className="mt-3 block text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            {t('go_by')}
          </span>
          <input className={inputCls} value={goBy} onChange={(e) => setGoBy(e.target.value)} />
        </label>
        <label className="mt-3 flex items-start gap-2 text-sm text-tcs-text-secondary">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 h-5 w-5"
          />
          {t('confirm_check')}
        </label>
        <button
          type="button"
          disabled={busy || !confirmed}
          className={`${btnCls} mt-3`}
          onClick={() =>
            run(async () => {
              await portalPublic({
                action: 'confirm_business',
                token,
                legal_business_name: legalName.trim() || null,
                go_by_name: goBy.trim() || null,
                confirmed: true,
              });
            })
          }
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('confirm_btn')}
        </button>
      </Step>

      {/* Step 2 — COI */}
      <Step
        icon={<ShieldCheck className="h-5 w-5" />}
        title={t('step_coi')}
        done={meta.steps.coi_uploaded}
        doneLabel={t('step_done')}
      >
        <UploadBlock
          label={t('upload_coi_label')}
          hint={t('upload_hint')}
          file={coiFile}
          setFile={setCoiFile}
          busy={busy}
          buttonLabel={t('upload_btn')}
          onUpload={() =>
            run(async () => {
              if (!coiFile) return;
              await portalPublic({
                action: 'upload_doc',
                token,
                doc_type: 'COI',
                filename: coiFile.name,
                mime: coiFile.type,
                file_base64: await fileToBase64(coiFile),
              });
              setCoiFile(null);
            })
          }
        />
      </Step>

      {/* Step 3 — W-9 */}
      <Step
        icon={<FileText className="h-5 w-5" />}
        title={t('step_w9')}
        done={meta.steps.w9_uploaded}
        doneLabel={t('step_done')}
      >
        <UploadBlock
          label={t('upload_w9_label')}
          hint={t('upload_hint')}
          file={w9File}
          setFile={setW9File}
          busy={busy}
          buttonLabel={t('upload_btn')}
          onUpload={() =>
            run(async () => {
              if (!w9File) return;
              await portalPublic({
                action: 'upload_doc',
                token,
                doc_type: 'W9',
                filename: w9File.name,
                mime: w9File.type,
                file_base64: await fileToBase64(w9File),
              });
              setW9File(null);
            })
          }
        />
      </Step>

      {/* Step 4 — ACH payment authorization (stored as the Direct_Deposit document; item 871) */}
      <Step
        icon={<Landmark className="h-5 w-5" />}
        title={t('step_dd')}
        done={meta.steps.direct_deposit_submitted}
        doneLabel={t('step_done')}
      >
        <p className="mb-1 text-xs text-tcs-text">{t('dd_intro')}</p>
        <p className="mb-3 text-xs text-tcs-text-muted">{t('dd_note')}</p>
        <div className="space-y-3">
          <input className={inputCls} placeholder={t('dd_holder')} value={dd.holder}
            onChange={(e) => setDd({ ...dd, holder: e.target.value })} />
          <input className={inputCls} placeholder={t('dd_bank')} value={dd.bank}
            onChange={(e) => setDd({ ...dd, bank: e.target.value })} />
          <input className={inputCls} placeholder={t('dd_routing')} inputMode="numeric" value={dd.routing}
            onChange={(e) => setDd({ ...dd, routing: e.target.value })} />
          <input className={inputCls} placeholder={t('dd_account')} inputMode="numeric" value={dd.account}
            onChange={(e) => setDd({ ...dd, account: e.target.value })} />
          <select className={inputCls} value={dd.type}
            onChange={(e) => setDd({ ...dd, type: e.target.value })}>
            <option value="checking">{t('dd_checking')}</option>
            <option value="savings">{t('dd_savings')}</option>
          </select>
          <button
            type="button"
            disabled={busy || !dd.holder || !dd.bank || !dd.routing || !dd.account}
            className={btnCls}
            onClick={() =>
              run(async () => {
                await portalPublic({
                  action: 'submit_direct_deposit',
                  token,
                  account_holder: dd.holder,
                  bank_name: dd.bank,
                  routing_number: dd.routing,
                  account_number: dd.account,
                  account_type: dd.type,
                });
                setDd({ holder: '', bank: '', routing: '', account: '', type: 'checking' });
              })
            }
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('dd_btn')}
          </button>
        </div>
      </Step>

      {/* Step 5 — sign */}
      <Step
        icon={<PenLine className="h-5 w-5" />}
        title={t('step_sign')}
        done={meta.steps.agreement_signed}
        doneLabel={t('step_done')}
      >
        <p className="mb-3 text-xs text-tcs-text-muted">{t('sign_intro')}</p>
        {otpUnavailable && (
          <div className="mb-3 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-3 text-xs text-st-warn-fg-strong">
            {t('otp_unavailable')}
          </div>
        )}
        {!meta.verified ? (
          <div className="space-y-3">
            <button
              type="button"
              disabled={busy}
              className={btnCls}
              onClick={() =>
                run(async () => {
                  try {
                    await portalPublic({ action: 'request_otp', token });
                    setOtpSent(true);
                    setOtpUnavailable(false);
                  } catch (err) {
                    if (err instanceof PortalError && err.status === 503) {
                      setOtpUnavailable(true);
                      return;
                    }
                    throw err;
                  }
                })
              }
            >
              {t('send_code')}
            </button>
            {otpSent && (
              <div className="flex gap-2">
                <input
                  className={`${inputCls} flex-1`}
                  placeholder={t('code_label')}
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <button
                  type="button"
                  disabled={busy || code.length !== 6}
                  className="inline-flex min-h-[48px] items-center justify-center rounded-md bg-tcs-inverse px-4 text-base font-semibold text-tcs-inverse-ink disabled:opacity-50"
                  onClick={() =>
                    run(async () => {
                      await portalPublic({ action: 'verify_otp', token, code });
                      setCode('');
                    })
                  }
                >
                  {t('verify_btn')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="inline-flex items-center gap-1 text-sm text-st-ok-fg">
              <CheckCircle2 className="h-4 w-4" /> {t('verified')}
            </p>
            <input
              className={inputCls}
              placeholder={t('signed_name')}
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
            />
            <label className="flex items-start gap-2 text-sm text-tcs-text-secondary">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-5 w-5"
              />
              {t('consent')}
            </label>
            <button
              type="button"
              disabled={busy || signedName.trim().length < 2 || !consent}
              className={btnCls}
              onClick={() =>
                run(async () => {
                  await portalPublic({
                    action: 'sign_agreement',
                    token,
                    signed_name: signedName.trim(),
                    consent: true,
                  });
                })
              }
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('sign_btn')}
            </button>
          </div>
        )}
      </Step>
    </Shell>
  );
}

// ── layout bits ─────────────────────────────────────────────────────────────

interface ShellProps {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: string) => string;
  business?: string | null;
  children: React.ReactNode;
}
function Shell({ lang, setLang, t, business, children }: ShellProps) {
  return (
    <div className="min-h-full bg-tcs-surface px-4 py-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h1 className="text-lg font-bold text-tcs-text">{t('title')}</h1>
            <p className="text-xs text-tcs-text-muted">
              {t('subtitle')}
              {business ? ` · ${business}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
            className="rounded-md border border-tcs-border-strong bg-tcs-raised px-3 py-2 text-xs font-semibold text-tcs-text-secondary"
          >
            {lang === 'en' ? 'Español' : 'English'}
          </button>
        </div>
        <div className="text-sm text-tcs-text">{children}</div>
      </div>
    </div>
  );
}

interface StepProps {
  icon: React.ReactNode;
  title: string;
  done: boolean;
  doneLabel: string;
  children: React.ReactNode;
}
function Step({ icon, title, done, doneLabel, children }: StepProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-3 rounded-lg border border-tcs-border bg-tcs-raised p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-tcs-text">
          {icon} {title}
        </span>
        {done ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-st-ok-bg-strong px-2 py-1 text-[11px] font-semibold uppercase text-st-ok-fg-strong">
            <CheckCircle2 className="h-3.5 w-3.5" /> {doneLabel}
          </span>
        ) : (
          <span className="rounded-md bg-tcs-surface px-2 py-1 text-[11px] font-semibold uppercase text-tcs-text-muted">
            •
          </span>
        )}
      </button>
      {(open || !done) && <div className="mt-3">{children}</div>}
    </div>
  );
}

interface UploadBlockProps {
  label: string;
  hint: string;
  file: File | null;
  setFile: (f: File | null) => void;
  busy: boolean;
  buttonLabel: string;
  onUpload: () => void;
}
function UploadBlock({ label, hint, file, setFile, busy, buttonLabel, onUpload }: UploadBlockProps) {
  return (
    <div className="space-y-3">
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
          {label}
        </span>
        <input
          type="file"
          accept=".pdf,image/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-tcs-text-secondary file:mr-3 file:rounded-md file:border-0 file:bg-tcs-inverse file:px-4 file:py-3 file:text-sm file:font-semibold file:text-tcs-inverse-ink"
        />
        <span className="mt-1 block text-xs text-tcs-text-muted">{hint}</span>
      </label>
      <button type="button" disabled={busy || !file} className={btnCls} onClick={onUpload}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {buttonLabel}
      </button>
    </div>
  );
}
